"use client";
import React, { useState, useEffect } from "react";
import {
  FileText,
  Upload,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  Plus,
  X,
  Eye,
  Search,
  ChevronsLeft,
  ChevronsRight,
  User,
  AlertTriangle,
  FolderOpen,
  Shield,
  Briefcase,
  FileCheck,
  FileBadge,
  FileSpreadsheet,
  File
} from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useDocumentTypes } from "../context/DocumentTypeContext";
import { useDocuments } from "../context/DocumentContext";
import { CDN_URL } from "../config/apiConfig";

// ── Constants ─────────────────────────────────────────────────────────────────

const DOC_TYPE_ICONS = {
  "Offer Letter": Briefcase,
  "Experience Certificate": FileCheck,
  "Salary Slip": FileSpreadsheet,
  "Relieving Letter": FileBadge,
  "Promotion Letter": Shield,
  "Employment Verification": FileCheck,
  "NOC Letter": File,
  "Form 16": FileSpreadsheet,
  "PF Statement": FileSpreadsheet,
  "Appraisal Letter": FileBadge
};

const STATUS_OPTIONS = ["pending", "approved", "fulfilled", "rejected"];

const STATUS_LABEL = {
  pending: "pending",
  approved: "Approved",
  // processing: "Processing",
  fulfilled: "Fulfilled",
  rejected: "Rejected"
};

const STATUS_CLASS = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-green-100 text-green-700",
  // processing: "bg-blue-100 text-blue-700",
  fulfilled: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700"
};

const STATUS_DOT = {
  pending: "bg-yellow-500",
  approved: "bg-green-500",
  // processing: "bg-blue-500",
  fulfilled: "bg-green-500",
  rejected: "bg-red-500"
};

const AVATAR_COLORS = [
  "bg-indigo-500", "bg-green-500", "bg-blue-500",
  "bg-pink-500", "bg-yellow-500", "bg-sky-500",
  "bg-red-500", "bg-teal-500"
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0][0]?.toUpperCase();
  return parts[0][0]?.toUpperCase() + parts[parts.length - 1][0]?.toUpperCase();
};

const Avatar = ({ name }) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const bg = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  const fullName = name?.trim() || "User";
  return (
    <div className="relative group inline-flex">
      <div className={`w-7 h-7 flex items-center justify-center rounded-full text-white text-xs font-bold ${bg}`}>
        {getInitials(fullName)}
      </div>
      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black text-white text-[11px] px-2 py-1 rounded z-50 whitespace-normal max-w-[160px] break-words text-center">
        {fullName}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

export default function WorkplaceDocuments() {
  const { documentTypes, fetchDocumentTypes } = useDocumentTypes();

  const {
  documents,
  loading,
  fetchDocuments,
  createDocumentRequest,
  updateDocumentStatus,
  updateDocument 
} = useDocuments();


const requests = React.useMemo(() => {
  return documents.map((doc) => ({
    id: doc.id,

    documentType: doc.document_type_name,

    requestedBy: `${doc.first_name} ${doc.last_name}`,

    department: doc.department_name || "-",

    requestedDate: doc.create_audit_time,

    fulfilledDate: doc.approved_date || null,

    handledBy: doc.approved_by_name || "-",

    reason: doc.document_type,

    remarks: doc.remarks,

    status: doc.status,

    uploadedFile: doc.file_path
      ? {
          name: doc.file_path.split("/").pop(),
          url: doc.file_path,
          size: "",
        }
      : null,

    raw: doc,
  }));
}, [documents]);

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("request"); // "request" | "hr_upload" | "view"
  const [selectedReq, setSelectedReq] = useState(null);

  // Request form
  const [form, setForm] = useState({ documentType: "", reason: "", department: "", requestedBy: "" });

  // HR upload form
  const [hrForm, setHrForm] = useState({ status: "processing", remarks: "", handledBy: "" });
  const [uploadedFile, setUploadedFile] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterDocType, setFilterDocType] = useState("All");

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // useEffect(() => { setPage(1); }, [searchTerm, filterStatus, filterDocType]);

  // ── Status counts ──────────────────────────────────────────────────────────
  const statusCounts = React.useMemo(() => {
    const counts = {};
    STATUS_OPTIONS.forEach(s => { counts[s] = 0; });
    requests.forEach(r => { if (counts[r.status] !== undefined) counts[r.status]++; });
    return counts;
  }, [requests]);

  // ── Filtered rows ──────────────────────────────────────────────────────────
  const q = searchTerm.toLowerCase();
  const filteredRows = React.useMemo(() =>
    requests.filter(r =>
      (filterDocType === "All" || r.documentType === filterDocType) &&
      (filterStatus.length === 0 || filterStatus.includes(r.status)) &&
      (r.requestedBy?.toLowerCase().includes(q) ||
        r.documentType?.toLowerCase().includes(q) ||
        r.department?.toLowerCase().includes(q) ||
        String(r.id).includes(q))
    ), [requests, filterDocType, filterStatus, searchTerm]);

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage) || 1;
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [filteredRows.length, rowsPerPage]);
  const currentRows = filteredRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // ── Modals ─────────────────────────────────────────────────────────────────
  const openRequestModal = () => {
    setForm({ documentType: "", reason: ""});
    setModalType("request");
    setShowModal(true);
  };

  const openHRModal = (req) => {
  setSelectedReq(req);

  setHrForm({
    status: req.status || "pending",
    remarks: req.remarks || "",
    handledBy: req.handledBy || "",
  });

  setUploadedFile(req.uploadedFile || null);

  setModalType("hr_upload");
  setShowModal(true);
};

  const openViewModal = (req) => {
    setSelectedReq(req);
    setModalType("view");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setUploadedFile(null);
    setSelectedReq(null);
  };

  // ── Submit request ─────────────────────────────────────────────────────────
  const handleRequestSubmit = async (e) => {
  e.preventDefault();

  const loginUser = JSON.parse(localStorage.getItem("user"));

  const payload = {
    user_id: loginUser.id,
    document_type_id: form.documentTypeId,
    remarks: form.reason || "",
  };
  const response = await createDocumentRequest(payload);

  if (response.success) {
    await fetchDocuments();

    setForm({
      documentType: "",
      documentTypeId: "",
      reason: "",
      department: "",
      requestedBy: "",
    });

    closeModal();
  } else {
    alert(response.message);
  }
};

  // ── HR submit (upload + update status) ────────────────────────────────────
 const handleHRSubmit = async (e) => {
  e.preventDefault();

  const loginUser = JSON.parse(localStorage.getItem("user"));

  if (!uploadedFile) {
    alert("Please select a file.");
    return;
  }

  const payload = {
    status: hrForm.status,
    remarks: hrForm.remarks,
    update_audit_id: loginUser.id,
    approved_by:
      hrForm.status === "approved" ? loginUser.id : "",
    file: uploadedFile,
  };

  const response = await updateDocument(selectedReq.id, payload);

  if (response.success) {
    await fetchDocuments();
    closeModal();

    setUploadedFile(null);
    setHrForm({
      status: "",
      remarks: "",
    });
  } else {
    alert(response.message);
  }
};

  // ── File pick (simulated) ──────────────────────────────────────────────────

const handleFilePick = (e) => {
  const file = e.target.files[0];

  if (!file) return;

  console.log(file); // Should log File object

  setUploadedFile(file);
};

  // ── Pagination ─────────────────────────────────────────────────────────────
  const paginationButtons = () => {
    const start = Math.max(page - 1, 1);
    const end = Math.min(start + 2, totalPages);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(n => (
      <button key={n} onClick={() => setPage(n)}
        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${n === page ? "text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
        style={n === page ? { background: "linear-gradient(135deg, #2563eb, #d12465)" } : undefined}
      >{n}</button>
    ));
  };

  const DocIcon = ({ type, size = 16 }) => {
    const Icon = DOC_TYPE_ICONS[type] || FileText;
    return <Icon size={size} className="text-indigo-500" />;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="bg-sky-50 min-h-screen">
        <div className="px-6 py-6 sm:px-9 max-sm:px-4">

          {/* Header */}
          <div className="mb-5">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Documents</h1>
            <p className="text-sm text-gray-500 mt-1">
              Request official HR documents and track their status. HR can upload and fulfill requests here.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 gap-3 bg-white rounded-2xl border border-gray-100 px-4 py-4 shadow-md">
            <div className="flex flex-wrap items-center gap-3 w-full">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, document type, department..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="border border-gray-200 bg-gray-50 rounded-lg pl-8 pr-3 py-2 text-sm outline-none w-full focus:ring-2 focus:ring-[2563eb]/40 focus:border-[2563eb]/40 focus:bg-white transition"
                />
              </div>
              <select
                value={filterDocType}
                onChange={e => setFilterDocType(e.target.value)}
                className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[2563eb]/40 focus:border-[2563eb]/40 focus:bg-white transition"
              >
                <option value="All">All Document Types</option>
                {documentTypes
                  .filter(doc => doc.status === "active")
                  .map(doc => (
                    <option key={doc.id} value={doc.name}>
                      {doc.name}
                    </option>
                ))}
              </select>
            </div>

            {(searchTerm || filterStatus.length > 0 || filterDocType !== "All") && (
              <button
                onClick={() => { setSearchTerm(""); setFilterStatus([]); setFilterDocType("All"); }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap"
              >
                <X size={14} /> Clear Filters
              </button>
            )}

            <button
              onClick={openRequestModal}
              className="flex items-center justify-center gap-1.5 text-white px-6 py-2 rounded-lg text-sm font-medium transition w-full sm:w-auto whitespace-nowrap hover:opacity-90"
              style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
            >
              <Plus size={16} /> Request Document
            </button>
          </div>

          {/* Status summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {STATUS_OPTIONS.map(status => {
              const active = filterStatus.includes(status);
              return (
                <div key={status}
                  onClick={() => setFilterStatus(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status])}
                  className={`cursor-pointer p-3.5 rounded-2xl border bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col items-center justify-center gap-1.5 ${active ? "border-transparent ring-2 ring-[2563eb]" : "border-gray-100"}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status]}`} />
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{STATUS_LABEL[status]}</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-800">{statusCounts[status] || 0}</div>
                </div>
              );
            })}
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-4 sm:p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-sky-50">
                    {["ID", "Document Type", "Requested By", "Department", "Requested Date", "Status", "Handled By", "Uploaded File", "Actions"].map(h => (
                      <th key={h} className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentRows.length > 0 ? currentRows.map(req => (
                    <tr key={req.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors duration-150">
                      <td className="py-3 px-4 text-indigo-600">{req.id}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <DocIcon type={req.documentType} />
                          <span className="text-gray-800">{req.documentType}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Avatar name={req.requestedBy} />
                          <span className="text-gray-700 text-xs">{req.requestedBy}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-700">{req.department}</td>
                      <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{req.requestedDate}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full ${STATUS_CLASS[req.status]}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[req.status]}`} />
                          {STATUS_LABEL[req.status]}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600">{req.handledBy || <span className="text-gray-300">—</span>}</td>
                      <td className="py-3 px-4">
                        {req.uploadedFile ? (
                          <a
                            href={`${CDN_URL}/${req.uploadedFile.url}`}
                            download={req.uploadedFile.name}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-green-700 bg-green-50 border border-green-200 hover:bg-green-100 transition"
                            onClick={(e) => {
                              if (!req.uploadedFile.url) e.preventDefault();
                            }}
                          >
                            <Download size={12} />
                            {req.uploadedFile.name.length > 20
                              ? req.uploadedFile.name.slice(0, 20) + "…"
                              : req.uploadedFile.name}
                          </a>
                        ) : (
                          <span className="text-gray-300 text-xs">Not uploaded</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2 text-gray-600">
                          <button title="View" onClick={() => openViewModal(req)} className="hover:text-indigo-600 transition"><Eye size={16} /></button>
                          {/* HR Upload action — shown when not fulfilled/rejected */}
                          {req.status !== "rejected" && (
                            <button
                              title={req.status === "fulfilled" ? "Re-upload" : "HR: Handle Request"}
                              onClick={() => openHRModal(req)}
                              className="hover:text-blue-600 transition"
                            >
                              <Upload size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="9" className="text-center py-10 text-gray-400 text-sm">
                        No document requests found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 pt-4 border-t border-gray-100 gap-3">
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <span>Rows per page:</span>
                <select className="border border-gray-200 bg-gray-50 rounded-lg px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-[2563eb]/40 focus:border-[2563eb]/40 transition" value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
                  {[5, 10, 15].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="flex items-center space-x-1">
                <button onClick={() => setPage(1)} disabled={page === 1} className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"><ChevronsLeft size={16} /></button>
                <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1} className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"><MdKeyboardArrowLeft size={16} /></button>
                {paginationButtons()}
                <button onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages} className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"><MdKeyboardArrowRight size={16} /></button>
                <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"><ChevronsRight size={16} /></button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl relative max-h-[88vh] flex flex-col overflow-hidden">
            <button onClick={closeModal} className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 z-10"><X size={20} /></button>

            {/* ── REQUEST DOCUMENT MODAL ── */}
            {modalType === "request" && (
              <form onSubmit={handleRequestSubmit} className="flex flex-col min-h-0 max-h-[88vh]">
                <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b border-gray-100 shrink-0">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}>
                    <FileText size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Request Document</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Submit a request and HR will upload the document for you</p>
                  </div>
                </div>

                <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
                  {/* Document Type */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                      <FolderOpen size={14} className="text-gray-400" /> Document Type <span className="text-red-500">*</span>
                    </label>
                    <select
  value={form.documentTypeId}
  onChange={(e) =>
    setForm((prev) => ({
      ...prev,
      documentTypeId: e.target.value,
    }))
  }
  required
  className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[2563eb]/40 focus:border-[2563eb]/40 focus:bg-white transition"
>
  <option value="">Select document type...</option>

  {documentTypes
    .filter((doc) => doc.status === "active")
    .map((doc) => (
      <option key={doc.id} value={doc.id}>
        {doc.name}
      </option>
    ))}
</select>
                  </div>

                  {/* <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                        <User size={14} className="text-gray-400" /> Your Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.requestedBy}
                        onChange={e => setForm(f => ({ ...f, requestedBy: e.target.value }))}
                        required
                        placeholder="Full name"
                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[2563eb]/40 focus:border-[2563eb]/40 focus:bg-white transition"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                        <Briefcase size={14} className="text-gray-400" /> Department <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.department}
                        onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                        required
                        placeholder="Your department"
                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[2563eb]/40 focus:border-[2563eb]/40 focus:bg-white transition"
                      />
                    </div>
                  </div> */}

                  {/* Reason */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                      <AlertTriangle size={14} className="text-gray-400" /> Reason for Request <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows="3"
                      value={form.reason}
                      onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                      required
                      placeholder="Briefly explain why you need this document…"
                      className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[2563eb]/40 focus:border-[2563eb]/40 focus:bg-white transition"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
                  <button type="button" onClick={closeModal} className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 w-full sm:w-auto transition">Cancel</button>
                  <button type="submit" style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }} className="px-5 py-2.5 rounded-xl text-sm font-medium w-full sm:w-auto flex items-center justify-center gap-2 text-white shadow-sm hover:opacity-90">
                    <Plus size={16} /> Submit Request
                  </button>
                </div>
              </form>
            )}

            {/* ── HR UPLOAD MODAL ── */}
            {modalType === "hr_upload" && (
              <form onSubmit={handleHRSubmit} className="flex flex-col min-h-0 max-h-[88vh]">
                <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b border-gray-100 shrink-0">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}>
                    <Upload size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">HR: Handle Request</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Upload the document and update request status</p>
                  </div>
                </div>

                <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">

                  {/* Request Summary */}
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 flex items-start gap-3">
                    <FileText size={18} className="text-indigo-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-indigo-800">{selectedReq?.documentType}</p>
                      <p className="text-xs text-indigo-600 mt-0.5">Requested by <span className="font-medium">{selectedReq?.requestedBy}</span> · {selectedReq?.department}</p>
                      <p className="text-xs text-gray-500 mt-1 italic">"{selectedReq?.reason}"</p>
                    </div>
                  </div>

                  {/* Upload area */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                      <Upload size={14} className="text-gray-400" /> Upload Document
                    </label>
                    {uploadedFile ? (
                      <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle size={16} className="text-green-600" />
                          <div>
                            <p className="text-sm font-medium text-green-800">{uploadedFile.name}</p>
                            <p className="text-xs text-green-600">{uploadedFile.size}</p>
                          </div>
                        </div>
                        <button type="button" onClick={() => setUploadedFile(null)} className="text-gray-400 hover:text-red-500 transition"><X size={16} /></button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl py-6 px-4 cursor-pointer hover:border-[2563eb] hover:bg-[2563eb]/5 transition">
                        <Upload size={24} className="text-gray-400" />
                        <span className="text-sm text-gray-500">Click to upload document</span>
                        <span className="text-xs text-gray-400">PDF, DOC, DOCX up to 10MB</span>
                        <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleFilePick} />
                      </label>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                      <Clock size={14} className="text-gray-400" /> Update Status
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {["pending", "approved", "rejected"].map(s => (
                        <label key={s} className={`cursor-pointer select-none px-3 py-1.5 rounded-full text-xs font-medium border transition ${hrForm.status === s ? `${STATUS_CLASS[s]} border-transparent ring-2 ring-offset-1 ring-[2563eb]` : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"}`}>
                          <input type="radio" name="hr_status" value={s} checked={hrForm.status === s} onChange={e => setHrForm(f => ({ ...f, status: e.target.value }))} className="hidden" />
                          {STATUS_LABEL[s]}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Handled By */}
                  {/* <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                      <User size={14} className="text-gray-400" /> Handled By
                    </label>
                    <input
                      type="text"
                      value={hrForm.handledBy}
                      onChange={e => setHrForm(f => ({ ...f, handledBy: e.target.value }))}
                      placeholder="HR Team / Person name"
                      className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[2563eb]/40 focus:border-[2563eb]/40 focus:bg-white transition"
                    />
                  </div> */}

                  {/* Remarks */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                      <AlertTriangle size={14} className="text-gray-400" /> Remarks <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea
                      rows="2"
                      value={hrForm.remarks}
                      onChange={e => setHrForm(f => ({ ...f, remarks: e.target.value }))}
                      placeholder="Any notes for the employee…"
                      className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[2563eb]/40 focus:border-[2563eb]/40 focus:bg-white transition"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
                  <button type="button" onClick={closeModal} className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 w-full sm:w-auto transition">Cancel</button>
                  <button type="submit" style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }} className="px-5 py-2.5 rounded-xl text-sm font-medium w-full sm:w-auto flex items-center justify-center gap-2 text-white shadow-sm hover:opacity-90">
                    <CheckCircle size={16} /> Save & Update
                  </button>
                </div>
              </form>
            )}

            {/* ── VIEW MODAL ── */}
            {modalType === "view" && selectedReq && (
              <div className="flex flex-col min-h-0 max-h-[88vh]">
                <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b border-gray-100 shrink-0">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}>
                    <Eye size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Request Details</h3>
                    <p className="text-xs text-gray-500 mt-0.5">#{selectedReq.id} · {selectedReq.documentType}</p>
                  </div>
                </div>

                <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {[
                      ["Document Type", selectedReq.documentType],
                      ["Requested By", selectedReq.requestedBy],
                      ["Department", selectedReq.department],
                      ["Requested Date", selectedReq.requestedDate],
                      ["Handled By", selectedReq.handledBy || "—"],
                      ["Fulfilled Date", selectedReq.fulfilledDate || "—"],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{label}</p>
                        <p className="text-gray-800 font-medium mt-0.5">{value}</p>
                      </div>
                    ))}
                  </div>

                  <div>
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Status</p>
                    <span className={`inline-flex items-center gap-1.5 mt-1 px-3 py-1 text-xs font-semibold rounded-full ${STATUS_CLASS[selectedReq.status]}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[selectedReq.status]}`} />
                      {STATUS_LABEL[selectedReq.status]}
                    </span>
                  </div>

                  <div>
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Reason</p>
                    <p className="text-gray-700 text-sm mt-0.5">{selectedReq.reason}</p>
                  </div>

                  {selectedReq.remarks && (
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">HR Remarks</p>
                      <p className="text-gray-700 text-sm mt-0.5">{selectedReq.remarks}</p>
                    </div>
                  )}

                  {selectedReq.uploadedFile ? (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">Uploaded Document</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText size={16} className="text-green-600" />
                          <div>
                            <p className="text-sm font-medium text-green-800">{selectedReq.uploadedFile.name}</p>
                            <p className="text-xs text-green-600">{selectedReq.uploadedFile.size}</p>
                          </div>
                        </div>
                        <a
                          href={selectedReq.uploadedFile.url}
                          download={selectedReq.uploadedFile.name}
                          onClick={e => { if (selectedReq.uploadedFile.url === "#") e.preventDefault(); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 transition"
                        >
                          <Download size={12} /> Download
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                      <FolderOpen size={24} className="text-gray-300 mx-auto mb-1" />
                      <p className="text-sm text-gray-400">No document uploaded yet</p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end px-6 py-4 border-t border-gray-100 shrink-0">
                  <button onClick={closeModal} className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition">Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}